import { FieldMetaState } from 'react-final-form';
import { isEmail, isPhoneNumberNew, filterFreeDomains } from '@lucid/utils';

type ComposeValidators = (value: string | string[] | number, allValues?: object, meta?: FieldMetaState<any>) => string | undefined;

export const composeValidators = (...validators: ComposeValidators[]) =>
  (value: string | string[] | number, allValues?: object, meta?: FieldMetaState<any>) =>
    validators.reduce<string | undefined>(
      (error, validator) => error || validator(value, allValues, meta),
      undefined
    );

export const isRequired = (
  shouldCheckRequired?: boolean | null,
  customErrorMessage?: string | null
) =>
  (value: string | string[] | number): string | undefined => (
    shouldCheckRequired
      ? value
        ? undefined
        : customErrorMessage || 'Required'
      : undefined
  );

export const isValidPhoneNumber = (
  type: string,
  customErrorMessage?: string | null
) =>
  (value: string | string[] | number): string | undefined => {
    const isTelephoneType = type === 'tel';

    if (isTelephoneType && value) {
      if (isPhoneNumberNew.test(value as string)) return undefined;

      return customErrorMessage || 'Must be a valid phone number.';
    }

    return undefined;
  };

export const isValidEmail = (
  type: string,
  customErrorMessage?: string | null
) =>
  (value: string | string[] | number): string | undefined => {
    const isEmailType = type === 'email';

    if (isEmailType) {
      if (isEmail.test(value as string)) return undefined;

      return customErrorMessage || 'Must be a valid email.';
    }

    return undefined;
  };

export const isValidEnterpriseEmail = (
  type: string,
  shouldRestrictFreeDomains: boolean,
  customErrorMessage?: string | null,
  callback?: () => void,
  handleEnterpriseError?: (message: string) => void
) =>
  (value: string | string[] | number): string | undefined => {
    const isEmailType = type === 'email';

    if (isEmailType && shouldRestrictFreeDomains) {
      const errorMessage = customErrorMessage || 'Please enter a work email.';
      const { shouldRestrictDomain, currentDomain } = filterFreeDomains(value as string);

      if (shouldRestrictDomain) {
        const customEnterpriseErrorMessage = `Please enter a work email. It must be a domain other than ${currentDomain}`;
        if (handleEnterpriseError) handleEnterpriseError(customEnterpriseErrorMessage);

        return customEnterpriseErrorMessage || errorMessage;
      }

      if (!shouldRestrictDomain) {
        if (callback) {
          callback();
          return undefined;
        }

        return errorMessage;
      }

      return undefined;
    }

    return undefined;
  };
